import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

export interface SegmentedTab {
  id: string;
  label: string;
}

export type SegmentedSize = 'md' | 'sm';

interface SegmentedTabsProps {
  tabs: SegmentedTab[];
  activeId: string;
  onChange: (id: string) => void;
  /** 'md' (default) for page navigation; 'sm' for compact inline filters. */
  size?: SegmentedSize;
  className?: string;
}

// Track padding = the inset the Thumb must match on all sides. Segment padding
// and font size scale with it so 'sm' reads as a compact accessory, not nav.
const TRACK_PAD: Record<SegmentedSize, number> = { md: 1, sm: 0.5 };

const Track = styled('div', {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: SegmentedSize }>`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  padding: ${({ theme, size }) => theme.spacing(TRACK_PAD[size])};
  background: ${({ theme }) => theme.background.tertiary};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  margin-bottom: ${({ theme, size }) => (size === 'sm' ? '0' : theme.spacing(5))};
  max-width: 100%;
  overflow-x: auto;
`;

// The sliding highlight. Position/size is set imperatively from the active
// segment's measured geometry so it lands exactly under variable-width labels.
const Thumb = styled('div', {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: SegmentedSize }>`
  position: absolute;
  top: ${({ theme, size }) => theme.spacing(TRACK_PAD[size])};
  bottom: ${({ theme, size }) => theme.spacing(TRACK_PAD[size])};
  left: 0;
  width: 0;
  background: ${({ theme }) => theme.accent.primary};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  box-shadow: ${({ theme }) => theme.boxShadow.level1};
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`;

const Segment = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'size',
})<{ active: boolean; size: SegmentedSize }>`
  position: relative;
  z-index: 1;
  border: none;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  padding: ${({ theme, size }) => (size === 'sm' ? theme.spacing(1, 2.5) : theme.spacing(2, 4))};
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme, size }) => (size === 'sm' ? theme.font.size.xs : theme.font.size.sm)};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme, active }) => (active ? '#fff' : theme.font.color.secondary)};
  transition: color 0.2s ease;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.border.radius.pill};
  }
  &:hover {
    color: ${({ theme, active }) => (active ? '#fff' : theme.font.color.primary)};
  }
`;

/**
 * Segmented control with a sliding accent thumb under the active tab.
 * Widths are measured (not assumed equal) so labels of different lengths line
 * up. Re-measures on active change, tab-set change, container resize, and once
 * web fonts settle (self-hosted Inter can shift metrics after first paint).
 */
export function SegmentedTabs({ tabs, activeId, onChange, size = 'md', className }: SegmentedTabsProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const segRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [thumb, setThumb] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const measure = useCallback(() => {
    const track = trackRef.current;
    const active = segRefs.current[activeId];
    if (!track || !active) return;
    // offsetLeft is relative to the positioned Track, which is what Thumb.left uses.
    setThumb({ left: active.offsetLeft, width: active.offsetWidth });
  }, [activeId]);

  useLayoutEffect(() => {
    measure();
  }, [measure, tabs]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(track);
    return () => ro.disconnect();
  }, [measure]);

  useLayoutEffect(() => {
    // Inter loads async; remeasure once fonts are ready so the thumb tracks the
    // final text width rather than the fallback-font width.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts?.ready) return;
    let cancelled = false;
    fonts.ready.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
    };
  }, [measure]);

  return (
    <Track ref={trackRef} className={className} role="tablist" size={size}>
      <Thumb style={{ left: thumb.left, width: thumb.width }} size={size} aria-hidden />
      {tabs.map((t) => (
        <Segment
          key={t.id}
          ref={(el) => {
            segRefs.current[t.id] = el;
          }}
          active={activeId === t.id}
          size={size}
          role="tab"
          aria-selected={activeId === t.id}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </Segment>
      ))}
    </Track>
  );
}
